import { Endpoint } from '../models/enums';
import { DeferredPromise } from '../models/deferred-promise';
import { RestService } from '../services/rest/rest.service';
import { CommonService } from '../services/common/common.service';
import { TemplateType } from './template';
import { isUndefined, isString } from './utils';

export enum DrObjectType {
    UNKNOWN = 'UNKNOWN',
    // Valid values
    ACTIVE_RULE = 'ACTIVE_RULE',
    ALIAS_LIST = 'ALIAS_LIST',
    BATCH = 'BATCH',
    CODE_STAMP = 'CODE_STAMP',
    CONNECTOR = 'CONNECTOR',
    COMPARISONS = 'COMPARISONS',
    CUSTODIAN = 'CUSTODIAN',
    CUSTOM_FIELD = 'CUSTOM_FIELD',
    CUSTOM_WARNINGS_ERRORS = 'CUSTOM_WARNINGS_ERRORS',
    CORPORA = 'CORPORA',
    DOMAIN_LIST = 'DOMAIN_LIST',
    DYNAMIC_FOLDER = 'DYNAMIC_FOLDER',
    EXCLUDED_CONTENT = 'EXCLUDED_CONTENT', // a.k.a Email Signature Template
    EXCLUSION_SEARCH = 'EXCLUSION_SEARCH',  // on Analytic Settings
    EXPORT_CONNECTION = 'EXPORT_CONNECTION',
    EXPORT_DATA_AREA = 'EXPORT_DATA_AREA',
    EXPORT_FIELDS = 'EXPORT_FIELDS',
    EXPORT_STREAM = 'EXPORT_STREAM',
    GROUP = 'GROUP',
    REALM_NODE = 'REALM_NODE',
    LOAD_FILE_IMPORT = 'LOADFILE_SETTINGS',
    LOTUS_NOTES = 'LOTUS_NOTES',
    MT_SERVICES = 'MT_SERVICES',
    MEDIAID = 'MEDIAID',
    OCR_SETTING = 'OCR_SETTING',
    PATTERN_TOKEN_NAME = 'PATTERN_TOKEN_NAME',
    PROFILE = 'PROFILE',
    PROJECT = 'PROJECT',
    PROJECT_DATA_AREA = 'PROJECT_DATA_AREA',
    WORK_FLOWS = 'WORK_FLOWS',
    ROLE = 'ROLE',
    SYSTEM_ROLE = 'SYSTEM_ROLE',
    SAMPLE_SET = 'SAMPLE_SET',
    SAVED_SEARCH = 'SAVED_SEARCH',
    SEARCH_SETTINGS = 'SEARCH_SETTINGS',
    SLIPSHEET = 'SLIPSHEET',
    TAG = 'TAG',
    TEMPLATE = 'TEMPLATE',
    USER = 'USER',
    REVIEW_PERSISTED_HIGHLIGHT_CATEGORY_COLOR = 'REVIEW_PERSISTED_HIGHLIGHT_CATEGORY_COLOR',
    REVIEW_PERSISTED_HIGHLIGHT_CATEGORY_NAME = 'REVIEW_PERSISTED_HIGHLIGHT_CATEGORY_NAME',
    EXPORT_DATABASE_TABLE = 'EXPORT_DATABASE_TABLE',
    ORGANIZATION = 'ORGANIZATION',
    STORAGE = 'STORAGE',
    SERVICE = 'SERVICE',
    REEFREVIEWFOLDER = 'REEFREVIEWFOLDER'
}

export enum SupportedRegExType {
    GENERIC,
    PATTERNS,
    CONNECTOR,
    DATAAREA,
    EXPORT_DATABASE_TABLE,
    USER_GROUP,
    EMAIL,
    METADATAVIEW,
    ORGANIZATION
}

class ReservedWords {
    reservedWords = new Map<string, unknown>();

    constructor() {
        this.reservedWords.set('any', true);
        this.reservedWords.set('not', true);
        this.reservedWords.set('negative', true);
        this.reservedWords.set('on', true);
        this.reservedWords.set('off', true);
        this.reservedWords.set('unassigned', true);
    }
}

export class DrObject {

    static DefaultMaximumNameCharacters = 100;
    static DefaultMaximumDecriptionCharacters = 255;

    initialized = false;

    objectType: DrObjectType;
    regex: SupportedRegExType;
    maxNameLength: number;
    maxDescriptionLength: number;
    templateType: TemplateType;
    hasReservedWords: boolean;

    iconClass: string;
    iconColor: string;

    restService: RestService;
    commonService: CommonService;

    constructor(type?: DrObjectType, regex?: SupportedRegExType);
    constructor(type: DrObjectType, regex?: SupportedRegExType, templateType?: TemplateType, maxNameLength?: number, maxDescriptionLength?: number) {

        if (type) {
            this.objectType = type;
        }

        this.regex = SupportedRegExType.GENERIC;
        if (regex) {
            this.regex = regex;
        }

        this.maxNameLength = DrObject.DefaultMaximumNameCharacters;
        if (maxNameLength) {
            this.maxNameLength = maxNameLength;
        }

        this.maxDescriptionLength = DrObject.DefaultMaximumDecriptionCharacters;
        if (maxDescriptionLength) {
            this.maxDescriptionLength = maxDescriptionLength;
        }

        this.templateType = null;
        if (templateType) {
            this.templateType = templateType;
        }
    }

    static validateReservedWords(name: string): string {
        let errorMessage = null;
        const reservedWords = new ReservedWords();
        const testName = name.toLowerCase();
        testName.trim();
        if (reservedWords.reservedWords.get(testName) != null) {
            errorMessage = 'Cannot use the reserved name "' + testName + '".';
        }

        return errorMessage;
    }

    // TODO: move to a stringUtils class
    static trim(text: string): string {
        let trimmedText = null;
        if (!isUndefined(text) && text.length !== 0) {
            trimmedText = text.trim();
        }
        return trimmedText;
    }

    static isEmpty(text: string, trim?: boolean): boolean {
        let isEmpty = false;

        if ((text === null) || isUndefined(text)) {
            isEmpty = true;
        } else {
            let finalText = text;
            if ((trim === true) && (text.length !== 0)) {
                finalText = this.trim(text);
            }

            if (!isUndefined(finalText.length) && text.length === 0) {
                isEmpty = true;
            }
        }
        return isEmpty;
    }

    static trimLeadingWhiteSpaces(aString: string): string {
        aString.replace(/^\s+/, '');  // remove leading whitespaces
        return aString;
    }

    static isValueDirty(text: string, original: string): boolean {
        let isDirty = false;
        if (!isUndefined(text) && (text !== null) && isString(text)) {
            const trimmedText = text.trim();
            if (!isUndefined(original) && (original !== null)) {
                const trimmedOriginal = original.trim();
                if (trimmedOriginal !== trimmedText) {
                    isDirty = true;
                }
            } else {
                isDirty = true;
            }
        }
        return isDirty;
    }

    /*
     For complete details on objects and their associated regexs refer to confluence page : http://172.31.210.172:8090/x/yxSr
     Currently Tested Regex (list of INVALID characters), this is a negative test
    (.*)[!<>~\"#%&*+./:;=?@\\u005d\\u005b^{\\\\}|\\u201C\\u201D$](.*)         - generic regex
    (.*)[!<>~\"#%&*+./:;=?@\\u005d\\u005b^{\\\\}|\\u201C\\u201D$\\s](.*)      - pattern regex      - generic, but no whitespaces
    (.*)[!<>~\"#%&*+/:;=?@\\u005d\\u005b^{\\\\}|\\u201C\\u201D$\\s](.*)       - connector regex    - no whitespaces, allowed .
    (.*)[!<>~\"#%&*+/:;=?@\\u005d\\u005b^{\\\\}|\\u201C\\u201D$](.*)";        - data area regex    - connector, but allows whitespaces
    (.* [!<>~\"*+/:;=?@\\u005d\\u005b^{\\\\}|\\u201C\\u201D](.*)              - custodian -        - generic, but no . # % & $
    */

    public uiValidateName(name: string): string {
        let message: string;
        switch (this.regex) {
            case SupportedRegExType.METADATAVIEW: {
                if (new RegExp(/(.*)[!<>~\"*+/:;=?@\u005d\u005b^\\\\{}|\u201C\u201D](.*)/).test(name)) {
                    message = 'Contains one or more of these invalid characters: ! " * + / : ; < = > ? @ [ \\ ] ^ { | } ~ \u201c \u201d';
                    return message;
                }
                break;
            }
            case SupportedRegExType.PATTERNS: {
                if (new RegExp(/(.*)[!<>%~\"#%&*+./:;=?@\u005d\u005b^\\\\{}|\u201C\u201D\\$\s](.*)/).test(name)) {
                    message = 'Contains a space or one or more of these invalid characters: ! " # $ % & * + . / : ; < = > ? @ [ \\ ] ^ { | } ~ \u201c \u201d';
                }
                break;
            }
            case SupportedRegExType.CONNECTOR: {
                if (new RegExp(/(.*)[!<>%~\"'#%&*+/:;=?@\u005d\u005b^\\\\{}|\u201C\u201D\\$\s](.*)/).test(name)) {
                    message = 'Contains a space or one or more of these invalid characters: ! " \' # $ % & * + / : ; < = > ? @ [ \\ ] ^ { | } ~ \u201c \u201d';
                }
                break;
            }
            case SupportedRegExType.DATAAREA: {
                if (new RegExp(/(.*)[!<>%~\"#%&*+/:;=?@\u005d\u005b^\\\\{}|\u201C\u201D\\$](.*)/).test(name)) {
                    message = 'Contains one or more of these invalid characters: ! " # $ % & * + / : ; < = > ? @ [ \\ ] ^ { | } ~ \u201c \u201d';
                }
                break;
            }
            case SupportedRegExType.EXPORT_DATABASE_TABLE: {
                if ((new RegExp(/(^[a-zA-Z_][0-9a-zA-Z_]*)$/).test(name)) === false) {
                    message = 'Cannot contain spaces, a leading digit, or characters other than underscore, a-z, A-Z, and 0-9.';
                }
                break;
            }
            case SupportedRegExType.USER_GROUP: {
                if (new RegExp(/(.*)[!<>%~\"#%&*+,/:;=?@\u005d\u005b^\\\\{}|\u201C\u201D\\$](.*)/).test(name)) {
                    message = 'Contains one or more of these invalid characters: ! " # $ % & * + , / : ; < = > ? @ [ \\ ] ^ { | } ~ \u201c \u201d';
                }
                break;
            }
            case SupportedRegExType.EMAIL: {
                if (new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/).test(name) === false) {
                    message = 'Invalid format';
                }
                break;
            }
            case SupportedRegExType.ORGANIZATION: {
                if (new RegExp(/(.*)[,!<>%~\"'#%&*+/:;=?@\u005d\u005b^\\\\{}|\u201C\u201D\\$\s](.*)/).test(name)) {
                    message = 'Contains a space or one or more of these invalid characters: ! " \' # $ % & * + / : ; < = > ? @ [ \\ ] ^ { | } ~ \u201c \u201d ,';
                }
                break;
            }
            default: { // GENERIC
                if (new RegExp(/(.*)[!<>%~\"#%&*+./:;=?@\u005d\u005b^\\\\{}|\u201C\u201D\\$](.*)/).test(name)) {
                    message = 'Contains one or more of these invalid characters: ! " # $ % & * + . / : ; < = > ? @ [ \\ ] ^ { | } ~ \u201c \u201d';
                }
                break;
            }
        }
        return message;
    }

    public restValidateName(name: string, projectHandle: string): Promise<string> {
        let message = null;
        // This value is null unless your are request the validation of a template (when you are creating the template), not the entities within the template.
        // In this case, objectType ='TEMPLATE' and templateType='ANALYTICAL_SETTINGS' (or any one of the TemplateType enum definitions.
        const request = new DeferredPromise<string>();

        this.restService.post(Endpoint.viewManager_validateName,
            {
                name: name,
                organizationName: this.commonService.currentUser.customerName,
                projectHandle: projectHandle,
                objectType: this.objectType,
                templateType: this.templateType
            }).then((response) => {
                if (response.isValid === false) {
                    message = response.reason;
                }
            }).catch((response) => {
                request.reject(response);
            });

        request.resolve(message);
        return request.promise;
    }

    setInitialized(initialized: boolean): void {
        this.initialized = initialized;
    }

    isInitialized(): boolean {
        return this.initialized;
    }
}
